<script setup lang="ts">
import { computed } from 'vue';
import { PageName } from '@/api/tracking';
import AdpDocumentBasicInfo from '@/components/common/adp-document/basic-info.vue';
import { AdpDocument, ThemePreview } from '@/types';
import { isArticle } from '@/types';
import {
  articleClickedEvent,
  articleContextMenuClickedEvent,
} from '@/utils/actions/view';
import { getPublisher, getSiteType } from '@/utils/adpDocument';
import { useCurrentPathForTracking } from '@/utils/composables/useCurrentPathForTracking';
import { getSiteCategoryLabel } from '@/utils/siteCategories';
import { useStore } from '@/utils/vue';

const props = defineProps<{ adpDocument: ThemePreview; isCompact?: boolean }>();

const store = useStore();
const { getCurrentPathForTracking } = useCurrentPathForTracking();

const category = isArticle(props.adpDocument)
  ? getSiteCategoryLabel(props.adpDocument)
  : undefined;

const documentPublisher = computed(() => getPublisher(props.adpDocument));

const teamInfo = computed(() => store.state.teamInfo.teamInfo);
const enableAllianceMedia = computed(
  () => teamInfo.value?.enable_alliance_media ?? false,
);

const sendViewProps = computed(() => {
  return {
    adpDocument: props.adpDocument as AdpDocument,
    trackingBaseData: {
      pageName: 'theme' as PageName,
      pageUrl: getCurrentPathForTracking(),
    },
    siteType: getSiteType(props.adpDocument),
    enableAllianceMedia: enableAllianceMedia.value,
  };
});
</script>

<template>
  <a
    class="theme-preview-adp-document__content"
    :href="adpDocument.url"
    target="_blank"
    rel="noopener noreferrer"
  >
    <div class="adp-document-info">
      <AdpDocumentBasicInfo
        :adp-document="adpDocument"
        size="xm"
        @article-link-clicked="articleClickedEvent(sendViewProps, adpDocument)"
        @article-link-context-menu="
          articleContextMenuClickedEvent(sendViewProps, $event, adpDocument)
        "
      >
        <template #documentMediaName>
          <div class="document-media-name c-text-regular--s">
            <span class="text-content">
              {{ documentPublisher }}
            </span>
            <div
              v-if="!isCompact && category"
              class="media-type-label c-title-bold--xs"
            >
              {{ category }}
            </div>
          </div>
        </template>
      </AdpDocumentBasicInfo>
    </div>
    <div v-if="!isCompact" class="hit-keyword">
      <div
        class="hit-keyword__tag c-outlineTag c-outlineTag--small"
        v-for="keyword in [
          ...adpDocument.hit_keywords,
          ...(adpDocument.synonym_keywords || []),
        ]"
        :key="keyword"
      >
        {{ keyword }}
      </div>
    </div></a
  >
</template>

<style lang="scss" scoped>
.theme-preview-adp-document__content {
  width: 100%;
  .adp-document-info {
    margin-left: 12px;
    margin-right: 12px;
  }
  .hit-keyword {
    display: flex;
    align-items: center;
    flex-direction: row;
    flex-wrap: wrap;
    margin: 8px 0 4px 10px;
  }
  .hit-keyword__tag {
    display: inline-block;
    max-width: 100%;
    box-sizing: border-box;
    margin: 0 4px 4px 0;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }
  .document-media-name {
    display: flex;
    max-width: 100%;
    gap: 4px;
    flex-wrap: wrap;
    font-weight: 700;
    .text-content {
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
    }
    .media-type-label {
      padding: 1px 8px;
      color: $color-gray800;
      background-color: $color-gray200;
      white-space: nowrap;
      border-radius: 2px;
    }
  }
}
</style>
